import moment from 'moment';
import { getShortReadableDateTime } from './date';
import 'moment/dist/locale/fr';

moment.locale('fr');

/**
 * Wrapper for moment library
 * Use it as a mixin and directly call the methods defined below
 */
export const momentMixin = {
  data() {
    return {
      monthNamesList: [ 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december' ],
    };
  },
  methods: {
    getReadableDate(date = undefined, delimiter = ',', onlyDateIfMidnight = false) {
      const momentDate = moment(date);
      let dateFormat = '';
      if (onlyDateIfMidnight === true && momentDate.hour() === 0 && momentDate.minute() === 0) {
        dateFormat = 'dddd DD MMMM YYYY';
      } else {
        dateFormat = `dddd DD MMMM YYYY${delimiter} HH:mm`;
      }
      const formattedDate = momentDate.format(dateFormat);

      return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    },
    getSimpleDate(date) {
      return moment(date).format('YYYY-MM-DD');
    },
    getSimpleDateIso(date) {
      const momentDate = moment(date);
      return {
        iso: momentDate.toISOString(),
        unix: momentDate.unix(),
      };
    },
    getSimpleDateFR(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    getSimpleDateWithTime(date, hours, minutes) {
      const momentDate = moment(date);
      momentDate.set({ hour: hours, minute: minutes });
      return momentDate.format('YYYY-MM-DD HH:mm');
    },
    getSimpleDateFrFirstDayNextMonth(date = undefined) {
      return this.getSimpleDateFR(moment(date).add(1, 'month').startOf('month'));
    },
    getShortReadableDateTime(date = undefined) {
      // 2 jan 2023 - 18:30
      return getShortReadableDateTime(date);
    },
    getHumanDate(date = undefined) {
      return moment(date).format('LL');
    },
    /**
     * Get numeric day and month
     * Ex : 04/08
     * @param date
     */
    getDayMonth(date = undefined) {
      return moment(date).format('DD/MM');
    },
    getHumanDay(date = undefined) {
      const formattedDate = moment(date).format('ddd DD/MM').replace('.', '');
      return formattedDate.charAt(0) + formattedDate.slice(1);
    },
    /**
     * Short human-readable month from a date
     * Ex : février 2025
     */
    getHumanMonth(date = undefined) {
      return moment(date).format('MMMM YYYY');
    },
    /**
     * Short human-readable day from a date
     * Ex : lun 17 avr
     * @param date
     */
    getShortHumanDay(date = undefined) {
      return moment(date).format('ddd DD MMM').replaceAll('.', '');
    },

    /**
     * Return a time from a date
     * Ex : 18:45
     * @param date
     */
    getTime(date = undefined) {
      return moment(date).format('HH:mm');
    },
    getIsoWithLocalTimezone(date) {
      return moment(date).toISOString(true);
    },
    /**
     * Takes a moment date, return true if that date is before current date
     * @param date
     */
    isBeforeToday(date) {
      if (!date) {
        return null;
      }
      return moment(date).isBefore(moment());
    },
    /**
     * Takes 2 dates, and checks if the first one is before the second one
     * @param date
     * @param dateToCompare
     */
    isBefore(date, dateToCompare) {
      if (!date || !dateToCompare) {
        return null;
      }
      return moment(date).isBefore(moment(dateToCompare));
    },
    /**
     * Takes 2 dates, and checks if they are the same
     * @param date1
     * @param date2
     * @param precision
     */
    isSame(date1, date2, precision = 'minute') {
      if (!date1 || !date2) {
        return null;
      }
      return moment(date1).isSame(moment(date2), precision);
    },
    /**
     * Checks if the date is today
     * @param date
     */
    isToday(date) {
      return moment(date).isSame(moment(), 'day');
    },
    /**
     * Checks if the date is today or tomorrow
     * @param date
     */
    isTodayOrTomorrow(date) {
      const momentDate = moment(date);

      return this.isToday(date) || momentDate.isSame(moment().add(1, 'days'), 'day');
    },
    /**
     * Checks if the date is near in the future, between now and the `amount` of `unit`
     * @param date
     * @param amount  The amount of time between now and the future. By default: 24.
     * @param unit    The unit used to check if the date is near in the future. By default: hours.
     */
    isNearInFuture(date, amount = 24, unit = 'hours') {
      const momentDate = moment(date);

      return momentDate.isBetween(moment(), moment().add(amount, unit));
    },
    /**
     * Takes one date and returns the number of minutes between this date and now
     * @param date
     */
    getMinutesUntilNow(date) {
      const momentDate = moment(date);
      return moment().diff(momentDate, 'minutes');
    },
  },
};
